import { Component, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { StoreService } from '../../store.service';
import { DealerService } from '../../dealer.service';
import { UtilitiesService } from 'src/app/core/utilities.service';
import { AiInsightSection, AiSectionConfig, parseAiInsightSections } from 'src/app/core/ai-summary-parser';
import { TranslationService } from 'src/app/core/translation.service';

/**
 * The backend builds the workbook and returns it with a Content-Disposition
 * filename, exactly like /order/export_orders. Change this one line if the
 * endpoint lands on a different path.
 */
const PROFIT_LOST_EXPORT_PATH = '/report/profit_lost/export';

/**
 * One heading in the Landing Page cell. `expanded` is view state rather than
 * data — rows are rebuilt on every load, so a collapsed group reopens closed.
 */
interface UrlGroup {
  label: string;
  urls: string[];
  expanded: boolean;
}

interface RevenueLostRow {
  landing_page_url_external: string;
  landing_page_id: string;
  total_abandoned_cart: number;
  currency: string;
  total_lost_revenue: number;
  avg_cart_value: number;
  total_payment_failed: number;
  /**
   * The page and its checkout link, each under its own heading. A slug answers
   * on the store subdomain and on every verified custom domain, so either list
   * can hold several URLs; only the first shows until the group is expanded.
   */
  urlGroups: UrlGroup[];
}

/**
 * The prompt asks for these English headings word for word, in every dashboard
 * language, and the card titles are translated here. The translated phrases are
 * only a fallback for when the model translates a heading anyway.
 */
const AI_SUMMARY_SECTIONS: AiSectionConfig[] = [
  {
    theme: 'overview',
    title: 'Executive Overview',
    icon: 'fi-rr-chart-pie-alt',
    phrases: ['executive overview', 'overview', '整体概览', '执行概览', '概览', '整體概覽', '執行概覽', '概覽', 'ikhtisar eksekutif', 'ikhtisar'],
  },
  {
    theme: 'performers',
    title: 'Top Revenue Leakage Pages',
    icon: 'fi-rr-shopping-cart-check',
    phrases: ['top revenue leakage', 'revenue leakage', 'top leakage', 'leakage pages', 'top performers', '收入流失', '营收流失', '營收流失', '流失最多', 'kebocoran pendapatan', 'halaman kebocoran'],
  },
  {
    theme: 'friction',
    title: 'Payment Failures & Bottlenecks',
    icon: 'fi-rr-exclamation',
    phrases: ['payment failures & bottlenecks', 'payment failures', 'payment failure', 'bottlenecks', 'friction', 'drop-off points', '付款失败', '付款失敗', '支付失败', '瓶颈', '瓶頸', 'kegagalan pembayaran', 'hambatan'],
  },
  {
    theme: 'recommendations',
    title: 'Actionable Recovery Recommendations',
    icon: 'fi-rr-bulb',
    phrases: ['actionable recovery recommendations', 'recovery recommendations', 'actionable recommendations', 'recommendations', 'action plan', 'key takeaways', '挽回建议', '挽回建議', '行动建议', '行動建議', '建议', '建議', 'rekomendasi'],
  },
];

/** Share of lost checkouts used for the "recover 1 in 10" scenario sent to the AI. */
const AI_RECOVERY_SCENARIO = 0.1;

@Component({
  selector: 'app-revenue-lost-report',
  templateUrl: './revenue-lost-report.component.html',
  styleUrls: ['./revenue-lost-report.component.scss'],
})
export class RevenueLostReportComponent implements OnInit {
  bearer: string;
  storeId: string;

  isLoading = true;
  loadFailed = false;

  isExporting = false;
  exportFailed = false;

  /** AI Assistant / Summary state */
  isGeneratingAiSummary = false;
  aiSummaryContent = '';
  aiSummaryError: string | null = null;
  showAiSummaryCard = false;
  isCopied = false;
  enableAIAssistant = false;
  /** Credits one AI summary costs, from the backend (AI_REPORT_SUMMARY_CREDIT); null until loaded or when not charged. */
  aiTokenCost: number | null = null;
  /** Purpose of the clicked AI button (its data-ai-purpose), sent to Mixpanel as ai_purpose. */
  aiPurpose = 'generate summary';
  parsedSections: AiInsightSection[] = [];
  private copyTimeout: any;

  /** Every row returned by the API, normalised. */
  rows: RevenueLostRow[] = [];
  /** Rows left after the currency tab + search box are applied, sorted. */
  filteredRows: RevenueLostRow[] = [];

  currencies: string[] = [];
  selectedCurrency = '';

  searchTerm = '';

  SORT_OPTIONS = [
    { display: 'Lost Revenue', value: 'total_lost_revenue' },
    { display: 'Abandoned Carts', value: 'total_abandoned_cart' },
    { display: 'Payment Failed', value: 'total_payment_failed' },
    { display: 'Avg Cart Value', value: 'avg_cart_value' },
  ];
  selectedSort = this.SORT_OPTIONS[0];

  summary = {
    lostRevenue: 0,
    abandonedCarts: 0,
    paymentFailed: 0,
    avgCartValue: 0,
    landingPages: 0,
  };

  /**
   * What each summary card actually counts, shown on the info icon beside its
   * label. Worth spelling out because an abandoned cart and a failed payment are
   * separate things here: a checkout that failed at payment adds to Lost Revenue
   * and to Payment Failed, but never to Abandoned Carts.
   */
  STAT_HINTS = {
    lostRevenue: 'Total value of the carts that were abandoned or whose payment failed.',
    abandonedCarts:
      'Carts left without a completed order. Checkouts that failed at payment are counted under Payment Failed instead.',
    paymentFailed: 'Checkout attempts that ended in a failed, rejected or expired payment.',
    avgCartValue: 'Lost revenue divided by the number of abandoned carts.',
  };

  /** Highest lost revenue in the current view, used to scale the share bars. */
  maxLostRevenue = 0;

  currentPage = 1;
  pageSize = 20;

  /** Identifies the newest in-flight report request; see loadReport. */
  private loadToken = 0;

  constructor(
    private dealerService: DealerService,
    private svc: StoreService,
    private utils: UtilitiesService,
    private sanitizer: DomSanitizer,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    this.bearer = localStorage.getItem('bearer');
    this.storeId = localStorage.getItem('store_id');
    this.loadAiCreditState();
    this.loadReport();
  }

  loadReport(): void {
    this.isLoading = true;
    this.loadFailed = false;

    // Retry can be pressed again while a fetch is still out, so only the newest
    // response is allowed to land.
    const token = ++this.loadToken;

    this.svc.getProfitLostReport(this.bearer, this.storeId).subscribe(
      (res: any) => {
        if (token !== this.loadToken) return;
        this.rows = this.extractRows(res).map((row) => this.normaliseRow(row));
        // applyFilters derives the tabs, so the first pass also picks the tab.
        this.applyFilters();
        this.isLoading = false;
      },
      () => {
        if (token !== this.loadToken) return;
        this.rows = [];
        this.filteredRows = [];
        this.isLoading = false;
        this.loadFailed = true;
      }
    );
  }

  /** The API wraps the list differently across report endpoints — accept the known shapes. */
  private extractRows(res: any): any[] {
    const candidates = [res?.data?.data, res?.data?.report, res?.data, res?.report, res];
    return candidates.find((value) => Array.isArray(value)) ?? [];
  }

  private normaliseRow(row: any): RevenueLostRow {
    return {
      landing_page_url_external: row?.landing_page_url_external ?? '',
      landing_page_id: `${row?.landing_page_id ?? ''}`,
      total_abandoned_cart: this.toNumber(row?.total_abandoned_cart),
      currency: `${row?.currency ?? ''}`.toUpperCase(),
      // The endpoint returns this key capitalised; keep the lower-case spelling working too.
      total_lost_revenue: this.toNumber(row?.Total_lost_revenue ?? row?.total_lost_revenue),
      avg_cart_value: this.toNumber(row?.avg_cart_value),
      total_payment_failed: this.toNumber(row?.total_payment_failed),
      urlGroups: this.toUrlGroups(row),
    };
  }

  private toNumber(value: any): number {
    const parsed = parseFloat(`${value ?? 0}`.replace(/,/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * The cell keeps the page's own addresses, its checkout link's, and the address
   * the cart was actually captured on under separate headings — they are
   * different destinations and the dealer needs to tell them apart. Each is shown
   * only when the row carries it, so a row with one heading is normal.
   */
  private toUrlGroups(row: any): UrlGroup[] {
    const groups: UrlGroup[] = [];
    // An external URL is often one of the resolved addresses too. Listing it
    // twice under two headings would read as a bug, so later groups only keep
    // what earlier ones did not already show.
    const seen = new Set<string>();

    const add = (label: string, raw: any): void => {
      const urls = this.toUrlList(raw).filter((url) => !seen.has(url));
      if (!urls.length) return;
      urls.forEach((url) => seen.add(url));
      groups.push({ label, urls, expanded: false });
    };

    add('Page Url', row?.page_url);
    add('Checkout Link', row?.checkout_link);
    add('External URL', row?.landing_page_url_external);

    return groups;
  }

  /** Accepts a list, a lone string from older responses, or nothing. */
  private toUrlList(raw: any): string[] {
    return (Array.isArray(raw) ? raw : [raw])
      .map((url) => `${url ?? ''}`.trim())
      .filter((url) => !!url);
  }

  /**
   * Currencies ordered by how much data each one carries — most landing pages
   * first, then most lost revenue, then alphabetically so the tab order stays
   * stable when two currencies tie.
   */
  private currenciesByVolume(rows: RevenueLostRow[]): string[] {
    const tally = new Map<string, { rows: number; lostRevenue: number }>();

    rows.forEach((row) => {
      if (!row.currency) return;
      const bucket = tally.get(row.currency) ?? { rows: 0, lostRevenue: 0 };
      bucket.rows += 1;
      bucket.lostRevenue += row.total_lost_revenue;
      tally.set(row.currency, bucket);
    });

    return Array.from(tally.entries())
      .sort(
        (a, b) =>
          b[1].rows - a[1].rows ||
          b[1].lostRevenue - a[1].lostRevenue ||
          a[0].localeCompare(b[0])
      )
      .map(([currency]) => currency);
  }

  /**
   * Re-derives the tabs from the rows the endpoint returned and keeps the
   * selection honest against them.
   */
  private syncCurrencies(rows: RevenueLostRow[]): void {
    this.currencies = this.currenciesByVolume(rows);

    // A single currency needs no tab, and with no tab there is nothing to filter
    // on — the summary already labels itself through summaryCurrency.
    if (this.currencies.length < 2) {
      this.selectedCurrency = '';
      return;
    }

    // Keep the dealer's tab when the data still has it, otherwise open on the
    // richest one. Leaving this blank while several tabs show would sum money
    // across currencies.
    if (this.currencies.indexOf(this.selectedCurrency) === -1) {
      this.selectedCurrency = this.currencies[0];
    }
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();

    // Currency tab first, then the search box.
    this.syncCurrencies(this.rows);

    this.filteredRows = this.rows
      .filter((row) => !this.selectedCurrency || row.currency === this.selectedCurrency)
      // Landing page only: with the Checkout ID column hidden, matching on it
      // would surface rows whose visible fields contain nothing of the term. Any
      // URL in any group counts, including ones behind a collapsed group.
      .filter(
        (row) =>
          !term ||
          row.urlGroups.some((group) =>
            group.urls.some((url) => url.toLowerCase().includes(term))
          )
      )
      .sort((a, b) => b[this.selectedSort.value] - a[this.selectedSort.value]);

    this.maxLostRevenue = this.filteredRows.reduce(
      (max, row) => Math.max(max, row.total_lost_revenue),
      0
    );

    const carts = this.filteredRows.reduce((sum, row) => sum + row.total_abandoned_cart, 0);
    const lostRevenue = this.filteredRows.reduce((sum, row) => sum + row.total_lost_revenue, 0);

    this.summary = {
      lostRevenue,
      abandonedCarts: carts,
      paymentFailed: this.filteredRows.reduce((sum, row) => sum + row.total_payment_failed, 0),
      avgCartValue: carts > 0 ? lostRevenue / carts : 0,
      landingPages: this.filteredRows.length,
    };

    this.currentPage = 1;
  }

  selectCurrency(currency: string): void {
    this.selectedCurrency = currency;
    this.applyFilters();
  }

  selectSort(option: { display: string; value: string }): void {
    this.selectedSort = option;
    this.applyFilters();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applyFilters();
  }

  /**
   * The workbook is built server-side and streamed back, so the currency tab and
   * search box travel as query params rather than filtering the rows on screen.
   */
  downloadExcel(): void {
    if (!this.filteredRows.length || this.isExporting) return;

    const param = {
      store_id: this.storeId,
      currency: this.selectedCurrency || 'ALL',
      search: this.searchTerm.trim(),
      type: 'excel',
    };

    this.isExporting = true;
    this.exportFailed = false;

    this.svc
      .downloadSpreadsheet(this.bearer, PROFIT_LOST_EXPORT_PATH, param)
      .catch((err) => {
        this.exportFailed = true;
        console.error('[revenue-lost-report] export failed', err);
      })
      .then(() => (this.isExporting = false));
  }

  /** Currency shown on the summary cards — blank when the rows mix currencies. */
  get summaryCurrency(): string {
    return this.selectedCurrency || (this.currencies.length === 1 ? this.currencies[0] : '');
  }

  get pagedRows(): RevenueLostRow[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredRows.slice(start, start + this.pageSize);
  }

  get start(): number {
    return (this.currentPage - 1) * this.pageSize;
  }

  get end(): number {
    return Math.min(this.start + this.pageSize, this.filteredRows.length);
  }

  formatAmount(value: number): string {
    return (value ?? 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  formatCount(value: number): string {
    return (value ?? 0).toLocaleString();
  }

  /** Width of the share bar under each lost-revenue figure. */
  lossShare(row: RevenueLostRow): number {
    if (!this.maxLostRevenue) return 0;
    return Math.max(2, Math.round((row.total_lost_revenue / this.maxLostRevenue) * 100));
  }

  /** Strips the protocol so the path stays readable on a narrow screen. */
  displayUrl(url?: string): string {
    if (!url) return '-';
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }

  /** Loads the AI Summary price (from the backend config) and whether the button can be used. */
  loadAiCreditState(): void {
    this.dealerService.getAiCreditState('AI_REPORT_SUMMARY_CREDIT').subscribe(({ enabled, tokenCost }) => {
      this.enableAIAssistant = enabled;
      this.aiTokenCost = tokenCost;
    });
  }

  /**
   * Prepares the report data for the AI prompt (GENERATE_REVENUE_LOST_REPORT_SUMMARY).
   * Every derived figure — shares, the payment-failure share, the recovery scenario — is
   * worked out here so the model only explains numbers, never calculates them. Amounts
   * carry the currency of the tab in view; the summary only ever covers one currency
   * because a store with several always has a tab selected.
   * The labels are referenced by the prompt template.
   */
  prepareReportSummaryPayload(): string {
    const rows = this.filteredRows;
    const currency = this.summaryCurrency || (rows[0]?.currency ?? '');
    const { lostRevenue, abandonedCarts, paymentFailed, avgCartValue } = this.summary;
    const lostCheckouts = abandonedCarts + paymentFailed;

    const money = (v: number) =>
      `${currency ? currency + ' ' : ''}${(v ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    // Estimates are rounded to whole amounts: they do not have cent-level precision
    const estimate = (v: number) => `${currency ? currency + ' ' : ''}${Math.round(v ?? 0).toLocaleString('en-US')}`;
    const count = (v: number) => Math.round(v ?? 0).toLocaleString('en-US');
    const share = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '0%');
    const urlOf = (r: RevenueLostRow) => r.urlGroups[0]?.urls[0] || r.landing_page_url_external || r.landing_page_id;

    const byLoss = [...rows].sort(
      (a, b) => b.total_lost_revenue - a.total_lost_revenue || b.total_abandoned_cart - a.total_abandoned_cart
    );
    const top3Loss = byLoss.slice(0, 3).reduce((sum, r) => sum + r.total_lost_revenue, 0);

    // Value of one lost checkout on a page; the report's own avg_cart_value divides by carts only
    const perCheckout = (r: RevenueLostRow) => {
      const n = r.total_abandoned_cart + r.total_payment_failed;
      return n > 0 ? r.total_lost_revenue / n : 0;
    };

    const leakageLines = byLoss.slice(0, 3).map((r, i) => {
      const n = r.total_abandoned_cart + r.total_payment_failed;
      return `${i + 1}. URL: ${urlOf(r)} | Lost revenue: ${money(r.total_lost_revenue)} (${share(r.total_lost_revenue, lostRevenue)} of all lost revenue)`
        + ` | Abandoned carts: ${count(r.total_abandoned_cart)} | Failed payments: ${count(r.total_payment_failed)}`
        + ` (${share(r.total_payment_failed, n)} of this page's lost checkouts)`
        + ` | Value per lost checkout: ${money(perCheckout(r))}`
        + ` | Recovering 1 in 10 lost checkouts would bring back about ${estimate(r.total_lost_revenue * AI_RECOVERY_SCENARIO)}`;
    });

    // Payment problems: failed payments are customers who already decided to buy
    const failureRows = rows
      .filter((r) => r.total_payment_failed > 0)
      .sort((a, b) => b.total_payment_failed - a.total_payment_failed || b.total_lost_revenue - a.total_lost_revenue);
    const failureLines = failureRows.slice(0, 3).map((r, i) => {
      const n = r.total_abandoned_cart + r.total_payment_failed;
      return `${i + 1}. URL: ${urlOf(r)} | Failed payments: ${count(r.total_payment_failed)} (${share(r.total_payment_failed, n)} of this page's lost checkouts)`
        + ` | Estimated value of the failed payments: about ${estimate(r.total_payment_failed * perCheckout(r))}`
        + ` | Abandoned carts: ${count(r.total_abandoned_cart)}`;
    });

    const failedValue = rows.reduce((sum, r) => sum + r.total_payment_failed * perCheckout(r), 0);

    const lines = [
      'OVERVIEW',
      `Currency: ${currency || 'not specified'}`,
      `Landing pages with lost sales: ${count(rows.length)}`,
      `Total lost revenue: ${money(lostRevenue)}`,
      `Lost checkouts: ${count(lostCheckouts)} (abandoned carts plus failed payments)`,
      `Abandoned carts: ${count(abandonedCarts)} (${share(abandonedCarts, lostCheckouts)} of lost checkouts)`,
      `Failed payments: ${count(paymentFailed)} (${share(paymentFailed, lostCheckouts)} of lost checkouts)`,
      `Estimated value of failed payments: about ${estimate(failedValue)}`,
      `Average cart value (as shown on the report): ${money(avgCartValue)}`,
      `Share of lost revenue from the top 3 pages: ${share(top3Loss, lostRevenue)}`,
      `Recovering 1 in 10 lost checkouts would bring back about: ${estimate(lostRevenue * AI_RECOVERY_SCENARIO)}`,
    ];
    if (this.searchTerm.trim()) {
      lines.push(`Filtered by search: "${this.searchTerm.trim()}"`);
    }

    return [
      lines.join('\n'),
      `PAGES LOSING THE MOST REVENUE\n${leakageLines.length ? leakageLines.join('\n') : 'None'}`,
      `PAGES WITH FAILED PAYMENTS\n${failureLines.length ? failureLines.join('\n') : 'None - no failed payments'}`,
    ].join('\n\n');
  }

  /** Sends the Mixpanel AI Generate event; ai_purpose comes from the clicked button's data-ai-purpose. */
  private trackAiGenerate(event: 'AI Generate Success' | 'AI Generate Fail', errorResponse?: string): void {
    this.svc.trackAiGenerateEvent(event, {
      aiPurpose: this.aiPurpose,
      storeId: this.storeId,
      token: this.aiTokenCost || 0,
      errorResponse,
    });
  }

  /** Triggers the AI Assistant to analyze and summarize the report. */
  generateAiSummary(event?: Event): void {
    if (this.isGeneratingAiSummary) return;
    this.aiPurpose = (event?.currentTarget as HTMLElement)?.dataset?.aiPurpose || this.aiPurpose;

    if (!this.filteredRows.length) {
      this.utils?.notify?.showInfo?.(
        'No revenue lost data available to summarize.',
        'Notice'
      );
      this.trackAiGenerate('AI Generate Fail', 'No revenue lost data available to summarize.');
      return;
    }

    this.isGeneratingAiSummary = true;
    this.aiSummaryError = null;
    this.showAiSummaryCard = true;
    this.isCopied = false;

    const reportData = this.prepareReportSummaryPayload();

    const params: any = {
      type: 'GENERATE_REVENUE_LOST_REPORT_SUMMARY',
      report_data: reportData,
      // Middleware writes the summary in this dashboard language; headings stay English for parseAiSummary()
      language_code: this.translationService.getLanguageCode(),
      desc: 'AI Revenue Lost Report Summary Generation',
    };

    this.svc.getAIGeneratedContent(params).subscribe(
      (res: any) => {
        this.isGeneratingAiSummary = false;
        let content = res?.data;
        if (typeof content === 'object') {
          content = JSON.stringify(content, null, 2);
        }
        this.aiSummaryContent = content || 'No summary generated.';
        this.parseAiSummary();
        this.loadAiCreditState();
        this.trackAiGenerate('AI Generate Success');
      },
      (err: any) => {
        this.isGeneratingAiSummary = false;
        console.error('[revenue-lost-report] AI generation failed', err);
        const serverMsg =
          err?.error?.message ||
          err?.error?.data?.error ||
          err?.error?.data?.message ||
          err?.error?.error;
        const errorMsg =
          (typeof serverMsg === 'string' && serverMsg ? serverMsg : null) ||
          this.utils?.getErrorMessageWithRefId?.(
            err,
            'Unable to generate AI summary at this time. Please check your AI Assistant credits or try again.'
          ) ||
          'Unable to generate AI summary at this time. Please check your AI Assistant credits or try again.';
        this.aiSummaryError = errorMsg;
        this.trackAiGenerate('AI Generate Fail', errorMsg);
      }
    );
  }

  /** Parses raw AI text into clean, structured sections for modern card rendering. */
  parseAiSummary(): void {
    // splitNotes: "Label: 62% - what it means" renders the figure bold and the explanation regular
    this.parsedSections = parseAiInsightSections(this.aiSummaryContent, AI_SUMMARY_SECTIONS, { splitNotes: true });
  }

  /** Copies generated AI summary text to clipboard. */
  copyAiSummary(): void {
    if (!this.aiSummaryContent) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(this.aiSummaryContent).then(() => {
        this.isCopied = true;
        this.utils?.notify?.showSuccess?.(
          'AI Summary has been copied to clipboard',
          'Copied!'
        );
        clearTimeout(this.copyTimeout);
        this.copyTimeout = setTimeout(() => {
          this.isCopied = false;
        }, 3000);
      });
    } else {
      this.utils?.copyToClipboard?.(this.aiSummaryContent, 'AI Summary');
    }
  }

  /** Closes/hides the AI summary card. */
  dismissAiSummary(): void {
    this.showAiSummaryCard = false;
  }
}
